import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Heart, PawPrint, Quote, Leaf, HandHeart, Home as HomeIcon, Shield, Clock, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import SectionLabel from "../components/SectionLabel";
import Counter from "../components/Counter";
import { getStats, getAnimals, getStories } from "../lib/api";

const HERO_IMG = "/hero-shelter.png";
const VOL_IMG = "https://images.unsplash.com/photo-1538399604295-204755ab3ad2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxhbmltYWwlMjByZXNjdWUlMjB2b2x1bnRlZXJ8ZW58MHx8fHwxNzc4NDI0ODM0fDA&ixlib=rb-4.1.0&q=85";

const CAMPAIGN_GOAL = 5000000; // ₹50,00,000 active campaign

export default function Home() {
  const [stats, setStats] = useState(null);
  const [animals, setAnimals] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
    getAnimals().then(setAnimals).catch(() => {});
    getStories().then(setStories).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* CINEMATIC HERO */}
      <section className="relative h-[88vh] min-h-[680px] overflow-hidden" data-testid="hero-section">
        {/* Background image with overlays */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Rescued dog with caretaker at Pawsomeland sanctuary"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 30%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/40 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent" />
        </div>

        {/* Hero text — sits in the LOWER portion of the image, well below the navbar */}
        <div className="relative z-10 h-full flex flex-col justify-end pb-28 lg:pb-32">
          <div className="max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl space-y-5 fade-up">
              <div className="inline-flex items-center gap-2 bg-terracotta/95 text-bone px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.25em] font-bold">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bone opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-bone"></span>
                </span>
                Active Campaign · Urgent
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light text-bone leading-[0.95] tracking-tight">
                300+ Rescued Dogs Have <em className="italic text-honey">Nowhere Else</em> To Go.
              </h1>
              <p className="text-lg lg:text-xl text-bone/85 max-w-2xl leading-relaxed">
                Many were injured, starving, abused, or left to die on the streets. Today, this shelter is the only family they have. Every meal, treatment, rescue, and safe night exists because compassionate people choose to help. Be the reason another dog survives.
              </p>
              <div className="flex items-center gap-5 pt-2 text-bone/75 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-honey" strokeWidth={1.5}/>
                  <span>80G tax exempt</span>
                </div>
                <div className="w-px h-4 bg-bone/30"/>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-honey" strokeWidth={1.5}/>
                  <span>Summer relief drive</span>
                </div>
                <div className="hidden sm:block w-px h-4 bg-bone/30"/>
                <div className="hidden sm:flex items-center gap-2">
                  <Users className="w-4 h-4 text-honey" strokeWidth={1.5}/>
                  <span>215+ supporters</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

        {/* Floating Campaign Card */}
        <div className="relative z-20 -mt-16 lg:-mt-20 pb-12 px-6 md:px-10 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div
              className="bg-white rounded-[2rem] lg:rounded-[2.5rem] shadow-[0_30px_80px_rgba(0,0,0,0.25)] p-7 lg:p-10 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center"
              data-testid="campaign-card"
            >
              {/* Left: Campaign details */}
              <div className="lg:col-span-7 space-y-5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-terracotta">Summer Relief Drive 2026</span>
                  <span className="w-1 h-1 rounded-full bg-ink/30"/>
                  <span className="text-xs text-ink/60">Bhadbhada Ghat · Bhopal</span>
                </div>
                <h2 className="font-serif text-3xl lg:text-4xl font-light text-forest leading-tight">
                  Save <em className="italic text-terracotta">300+ injured dogs</em> from homelessness
                </h2>

                {/* Progress */}
                {(() => {
                  const raised = stats?.total_donations_inr ?? 482000;
                  const pct = Math.min(Math.round((raised / CAMPAIGN_GOAL) * 100), 100);
                  return (
                    <div className="space-y-3 pt-2" data-testid="campaign-progress">
                      <div className="flex items-baseline justify-between flex-wrap gap-2">
                        <div>
                          <div className="font-serif text-3xl lg:text-4xl text-forest font-light">
                            ₹{raised < 100000 ? raised.toLocaleString("en-IN") : `${(raised / 100000).toFixed(2)}L`}
                            <span className="text-base text-ink/55 ml-2">raised</span>
                          </div>
                        </div>
                        <div className="text-sm text-ink/60">
                          Goal: <span className="font-semibold text-forest">₹{(CAMPAIGN_GOAL / 100000).toFixed(0)}L</span>
                          <span className="ml-3 inline-block bg-terracotta/10 text-terracotta px-2.5 py-0.5 rounded-full font-bold text-xs">{pct}%</span>
                        </div>
                      </div>
                      <div className="h-2.5 bg-bone-soft rounded-full overflow-hidden relative">
                        <div
                          className="absolute inset-y-0 left-0 bg-gradient-to-r from-terracotta to-honey rounded-full transition-all duration-1000"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs text-ink/60">
                        <div className="flex items-center gap-1.5">
                          <div className="flex -space-x-2">
                            {animals.slice(0, 3).map((a) => (
                              <img key={a.id} src={a.image} alt="" className="w-7 h-7 rounded-full border-2 border-white object-cover"/>
                            ))}
                          </div>
                          <span className="ml-1"><span className="font-semibold text-forest">{(stats?.donor_count ?? 215).toLocaleString("en-IN")}</span> people donated</span>
                        </div>
                        <span>14 days left</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Right: CTAs */}
              <div className="lg:col-span-5 space-y-3 lg:border-l lg:border-bone-soft lg:pl-10">
                <Link to="/donate" data-testid="hero-donate-cta" className="block">
                  <Button className="w-full bg-terracotta hover:bg-terracotta-deep text-bone rounded-2xl h-16 text-lg font-semibold tracking-wide group shadow-[0_10px_30px_rgba(200,90,64,0.35)]">
                    Donate Now
                    <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" strokeWidth={1.8}/>
                  </Button>
                </Link>
                <Link to="/adopt" data-testid="hero-sponsor-cta" className="block">
                  <Button variant="outline" className="w-full border-2 border-forest/15 bg-bone hover:bg-forest hover:text-bone hover:border-forest text-forest rounded-2xl h-14 text-base font-semibold">
                    <PawPrint className="w-4 h-4 mr-2" strokeWidth={1.8}/>
                    Sponsor a Dog
                  </Button>
                </Link>
                <div className="flex items-center justify-center gap-2 pt-3 text-xs text-ink/60">
                  <Shield className="w-3.5 h-3.5 text-terracotta" strokeWidth={1.8}/>
                  <span>100% secure · 80G tax receipt instantly</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* MARQUEE STRIP */}
      <section className="bg-forest text-bone py-6 overflow-hidden border-y border-forest" data-testid="impact-strip">
        <div className="marquee font-serif text-2xl lg:text-3xl">
          {Array(2).fill(null).map((_, i) => (
            <div key={i} className="flex gap-16 items-center shrink-0">
              <span>· Cage-free since 2018</span>
              <PawPrint className="w-5 h-5 text-terracotta" strokeWidth={1.5}/>
              <span>· 900+ lives saved</span>
              <PawPrint className="w-5 h-5 text-terracotta" strokeWidth={1.5}/>
              <span>· Bhopal sanctuary, 1 acre</span>
              <PawPrint className="w-5 h-5 text-terracotta" strokeWidth={1.5}/>
              <span>· 80G tax exempt</span>
              <PawPrint className="w-5 h-5 text-terracotta" strokeWidth={1.5}/>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION / 3 PILLARS */}
      <section className="py-24 lg:py-32" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <SectionLabel className="mb-6">What we believe</SectionLabel>
              <h2 className="font-serif text-4xl lg:text-5xl font-light text-forest leading-tight">
                Rescue is the easy part. <em className="italic text-terracotta">Dignity</em> is the work.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <p className="text-lg text-ink/75 leading-relaxed">
                Most shelters were built like prisons — concrete floors, metal bars, a 6×4 ft world. We tore that down. Our 1-acre sanctuary at Bhadbhada Ghat, Bhopal gives every resident grass under their paws, sun on their fur, and a human who knows their name.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: HomeIcon, title: "Cage-free shelter", body: "Open enclosures, real beds, sky overhead. Animals roam, sniff, sleep — the way they were always meant to." },
              { icon: HandHeart, title: "Medical first", body: "On-site vets, ICU and physio. Every rescue is sterilised, vaccinated and rehabilitated before being considered for adoption." },
              { icon: Leaf, title: "Community-led", body: "Local feeders, vendors, traffic cops — we work with the communities animals already belong to. Not against them." },
            ].map((p, i) => (
              <div
                key={i}
                className="group bg-white rounded-3xl p-8 border border-bone-soft hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)] transition-all duration-300"
                data-testid={`pillar-card-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-terracotta/10 flex items-center justify-center mb-6 group-hover:bg-terracotta group-hover:rotate-6 transition-all">
                  <p.icon className="w-6 h-6 text-terracotta group-hover:text-bone transition-colors" strokeWidth={1.5}/>
                </div>
                <h3 className="font-serif text-2xl text-forest mb-3">{p.title}</h3>
                <p className="text-ink/75 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 lg:py-28 bg-bone-soft/40" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
            {[
              { label: "Animals rescued", value: stats?.animals_rescued ?? 900 },
              { label: "Adopted into homes", value: stats?.animals_adopted ?? 100 },
              { label: "Active volunteers", value: stats?.volunteers ?? 20 },
              { label: "Funds raised (₹L)", value: Math.floor((stats?.total_donations_inr ?? 300000) / 100000), suffix: "L" },
            ].map((s, i) => (
              <div key={i} className="space-y-2 lg:border-l border-forest/15 lg:pl-6" data-testid={`stat-${i}`}>
                <div className="font-serif text-5xl lg:text-6xl font-light text-forest tracking-tight">
                  <Counter end={s.value} suffix={s.suffix || ""} />
                </div>
                <div className="text-sm text-ink/70 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED RESIDENTS */}
      <section className="py-24 lg:py-32" data-testid="residents-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <SectionLabel className="mb-5">Looking for a home</SectionLabel>
              <h2 className="font-serif text-4xl lg:text-5xl font-light text-forest leading-tight max-w-xl">
                Meet a few of our <em className="italic text-terracotta">residents</em>
              </h2>
            </div>
            <Link to="/adopt" className="text-forest font-medium link-underline" data-testid="residents-view-all-link">
              See everyone available →
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {animals.slice(0, 4).map((a, i) => (
              <Link
                to="/adopt"
                key={a.id}
                className={`group block ${i % 2 === 1 ? "lg:translate-y-8" : ""}`}
                data-testid={`featured-animal-${a.id}`}
              >
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden mb-4">
                  <img src={a.image} alt={a.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute top-4 left-4 bg-bone/95 px-3 py-1 rounded-full text-xs uppercase tracking-wider font-semibold text-forest">
                    {a.species} · {a.location}
                  </div>
                </div>
                <h4 className="font-serif text-2xl text-forest group-hover:text-terracotta transition-colors">{a.name}</h4>
                <p className="text-sm text-ink/70 mt-1 line-clamp-2">{a.story}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24 lg:py-32 bg-forest text-bone relative overflow-hidden" data-testid="testimonial-section">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 text-center relative">
          <Quote className="w-12 h-12 text-terracotta mx-auto mb-8" strokeWidth={1.2}/>
          <blockquote className="font-serif text-3xl lg:text-5xl font-light leading-snug text-bone">
            We adopted Tara from Pawsomeland four years ago. Watching her unlearn fear, run barefoot on grass, sleep belly-up on our couch — that is what a cage-free life gives back.
          </blockquote>
          <div className="mt-10 text-bone/80">
            <p className="font-medium">Priya & Anand Rao</p>
            <p className="text-sm text-bone/60 uppercase tracking-wider">Mumbai · Adopters since 2021</p>
          </div>
        </div>
      </section>

      {/* STORIES TEASER */}
      <section className="py-24 lg:py-32" data-testid="stories-teaser-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <SectionLabel className="mb-5">From the field</SectionLabel>
              <h2 className="font-serif text-4xl lg:text-5xl font-light text-forest leading-tight">
                Stories from <em className="italic text-terracotta">the sanctuary</em>
              </h2>
            </div>
            <Link to="/stories" className="text-forest font-medium link-underline" data-testid="stories-view-all-link">
              Read all stories →
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {stories.map((s) => (
              <Link to={`/stories/${s.id}`} key={s.id} className="group block" data-testid={`story-card-${s.id}`}>
                <div className="aspect-[4/3] rounded-3xl overflow-hidden mb-5">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold mb-2">{s.tag} · {s.date}</div>
                <h3 className="font-serif text-2xl text-forest group-hover:text-terracotta transition-colors leading-tight mb-2">{s.title}</h3>
                <p className="text-ink/70 text-sm line-clamp-2">{s.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-20" data-testid="cta-band">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="rounded-[2.5rem] bg-terracotta text-bone p-10 lg:p-16 grid lg:grid-cols-12 gap-8 items-center relative overflow-hidden">
            <img src={VOL_IMG} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-25 hidden lg:block"/>
            <div className="lg:col-span-8 relative">
              <h2 className="font-serif text-4xl lg:text-5xl font-light leading-tight">
                Your ₹500 feeds a rescue dog for a month.<br/>
                <em className="italic">What will you give?</em>
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end relative">
              <Link to="/donate" data-testid="cta-band-donate-button">
                <Button className="bg-bone text-terracotta hover:bg-honey hover:text-forest rounded-full h-14 px-8 text-base font-bold">
                  Donate now →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
